// src/components/ActiveTestView.js
import React, { useState, useEffect } from 'react';
import {
    ActiveTestWrapper,
    QuestionWrapper,
    AnswerOptionsGrid,
    AnswerButton,
    TestControls,
    ResultsWrapper
} from './PersonalityTestsStyles'; // Style importowane jak wcześniej
import { StyledButton } from '../main/CommonStyles';
import { ChevronLeft, ChevronRight, RefreshCcw } from 'lucide-react'; // Dodajemy ChevronRight

const ImportantDisclaimer = () => (
    <div className="important-disclaimer">
        <p><strong>Ważna Uwaga:</strong> Ten test ma charakter wyłącznie informacyjny i edukacyjny. <strong>NIE jest profesjonalnym narzędziem diagnostycznym i NIE zastępuje konsultacji ze specjalistą.</strong> Wyniki nie powinny być traktowane jako diagnoza. Jeśli masz obawy dotyczące swojego zdrowia psychicznego, skonsultuj się ze specjalistą.</p>
    </div>
);

const ActiveTestView = ({ testData, onBackToList }) => {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        setCurrentQuestionIndex(0);
        setAnswers({});
        setShowResults(false);
        setResult(null);
    }, [testData]);

    const handleAnswerSelect = (questionId, answerValue) => {
        setAnswers(prev => ({
            ...prev,
            [questionId]: answerValue,
        }));
        // Usunięto automatyczne przechodzenie do następnego pytania
    };

    const calculateResults = () => {
        if (!testData.scoringLogic) {
            setResult({ interpretation: "Logika oceny dla tego testu nie została jeszcze zaimplementowana." });
            return;
        }
        const calculatedResult = testData.scoringLogic(answers);
        setResult(calculatedResult);
    };

    const handleProceedToNext = () => {
        if (currentQuestionIndex < testData.questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        } else {
            // Ostatnie pytanie, oblicz i pokaż wyniki
            calculateResults();
            setShowResults(true);
        }
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const handleRetakeTest = () => {
        setCurrentQuestionIndex(0);
        setAnswers({});
        setShowResults(false);
        setResult(null);
    };

    if (showResults) {
        return (
            <ActiveTestWrapper>
                <div className="test-header">
                    <h2>Wyniki Testu: {testData.name}</h2>
                </div>
                <ImportantDisclaimer />
                <ResultsWrapper>
                    <h3>Twoje Wyniki</h3>
                    {result?.score !== undefined && (
                        <p className="result-score">
                            {testData.resultLabel || "Uzyskany wynik"}: {result.score} {testData.maxScore ? `/ ${testData.maxScore}` : ''}
                        </p>
                    )}
                    <div className="result-interpretation">
                        {result?.interpretation || "Brak interpretacji dla tego wyniku."}
                    </div>
                    <StyledButton onClick={handleRetakeTest} $secondary style={{ marginRight: '10px', marginTop: '20px' }}>
                        <RefreshCcw size={16} style={{ marginRight: '5px' }}/> Rozwiąż Ponownie
                    </StyledButton>
                    <StyledButton onClick={onBackToList} style={{ marginTop: '20px' }}>
                        <ChevronLeft size={16} style={{ marginRight: '5px' }} /> Wróć do Listy Testów
                    </StyledButton>
                </ResultsWrapper>
            </ActiveTestWrapper>
        );
    }

    const currentQuestion = testData.questions[currentQuestionIndex];
    // Sprawdzamy, czy dla bieżącego pytania jest zaznaczona odpowiedź
    const isCurrentQuestionAnswered = answers[currentQuestion.id] !== undefined;

    return (
        <ActiveTestWrapper>
            <div className="test-header">
                <h2>{testData.name}</h2>
                <p>{testData.instructions || "Wybierz odpowiedź, która najlepiej Cię opisuje."}</p>
            </div>

            {testData.showDisclaimerBeforeStarting && <ImportantDisclaimer />}

            <QuestionWrapper>
                <p className="question-text">({currentQuestionIndex + 1}/{testData.questions.length}) {currentQuestion.text}</p>
                <AnswerOptionsGrid>
                    {currentQuestion.options.map(option => (
                        <AnswerButton
                            key={option.value}
                            onClick={() => handleAnswerSelect(currentQuestion.id, option.value)}
                            $selected={answers[currentQuestion.id] === option.value}
                        >
                            {option.label}
                        </AnswerButton>
                    ))}
                </AnswerOptionsGrid>
            </QuestionWrapper>

            <TestControls>
                <StyledButton onClick={handlePreviousQuestion} disabled={currentQuestionIndex === 0} $secondary>
                    <ChevronLeft size={16} style={{ marginRight: '5px' }}/> Poprzednie
                </StyledButton>
                <p className="progress-text">Pytanie {currentQuestionIndex + 1} z {testData.questions.length}</p>
                <StyledButton onClick={handleProceedToNext} disabled={!isCurrentQuestionAnswered}>
                    {currentQuestionIndex === testData.questions.length - 1 ? 'Zobacz Wyniki' : 'Następne'}
                    {currentQuestionIndex !== testData.questions.length - 1 && <ChevronRight size={16} style={{ marginLeft: '5px' }}/>}
                </StyledButton>
            </TestControls>
            <StyledButton onClick={onBackToList} $secondary style={{ marginTop: '20px', display: 'block', marginLeft: 'auto', marginRight: 'auto', width: 'fit-content' }}>
                Przerwij i Wróć do Listy
            </StyledButton>
        </ActiveTestWrapper>
    );
};

export default ActiveTestView;